<script setup lang="ts">
import { setConfig } from '@barbanet/api-service'
import { useAppConfig } from '@barbanet/app-config'
import { PermissionRouteGuard, usePermission } from '@barbanet/permissions'

import { BHeader, BHeaderProps } from '@barbanet/ui-kit/components'
import { useExceptionHandler } from '@barbanet/ui-kit/composables'

import sidebarItems from './constants/sidebar-items'
import { postSwitchWorkspaceService } from './domains/user/services/user'
import { useAccountStore } from './stores/account-store'

const { apiServer, appVersion, apiServerDefaultVersion } = useAppConfig()
const accountStore = useAccountStore()
const { t, te } = useI18n()
const router = useRouter()
const { sync: syncPermissions } = usePermission()

setConfig({
	apiServer,
	appVersion: appVersion || undefined,
	apiServerDefaultVersion,
	credentials:
		accountStore.isLoggedIn && accountStore.credentials
			? {
					accessToken: accountStore.credentials.accessToken,
					refreshToken: accountStore.credentials.refreshToken
				}
			: null,
	onRefreshToken(newCredentials) {
		accountStore.setCredentials(newCredentials)
	},
	async onRevokeToken() {
		accountStore.logout()
	}
})

const syncWorkspace = async () => {
	if (accountStore.isLoggedIn) {
		try {
			await accountStore.syncAccountInfo()
			await syncPermissions()
		} catch (error) {
			useExceptionHandler(error)
		}
	}
}

const userInfo = computed(() => {
	if (accountStore.accountInfo.user && accountStore.accountInfo.currentRole) {
		return {
			displayName: accountStore.accountInfo.user.displayName,
			companyName: accountStore.accountInfo.currentRole.company.title,
			position: accountStore.accountInfo.currentRole.position
		} as BHeaderProps['userInfo']
	}

	return null
})

const menuItems = computed(() => {
	let items = [] as InstanceType<typeof BHeader>['$props']['menuItems']

	if (accountStore.accountInfo.roles.length > 1) {
		items = [
			...accountStore.accountInfo.roles.map((item) => {
				return {
					id: `--workspace--${item.company.id}`,
					text: item.company.title,
					classes:
						item.company.id === accountStore.accountInfo.currentRole?.company.id
							? 'border-inform! bg-inform-container!'
							: undefined
				}
			}),
			{
				id: 'separator',
				separator: true
			}
		]
	}

	items?.push({
		id: 'logout',
		text: te('shared.logout') ? t('shared.logout') : '',
		color: 'error',
		icon: 'logout-02'
	})

	return items
})

const switchingWorkspace = ref(false)
const switchWorkspace = async (companyId: string) => {
	switchingWorkspace.value = true

	try {
		const response = await postSwitchWorkspaceService(companyId)

		if (response && response.data) {
			accountStore.setCredentials(response.data.content)

			router.replace({ name: 'DashboardPage' })
			await syncWorkspace()
		}
	} catch (error) {
		useExceptionHandler(error)
	} finally {
		switchingWorkspace.value = false
	}
}

const onMenuItemClick = async (itemId: string) => {
	if (itemId === 'logout') {
		accountStore.logout()
		return
	}

	if (itemId.startsWith('--workspace--')) {
		const companyId = itemId.replace('--workspace--', '')

		if (companyId !== accountStore.accountInfo.currentRole?.company.id) {
			switchWorkspace(companyId)
		}
	}
}

const userInfoLoading = computed(
	() => accountStore.accountInfo.loading || switchingWorkspace.value
)

onMounted(async () => {
	await router.isReady()
	await syncWorkspace()
})
</script>

<template>
	<PermissionRouteGuard>
		<BLayout
			:sidebar-props="{
				items: sidebarItems,
				dashboardTo: { name: 'DashboardPage' }
			}"
			:header-props="{
				userInfoLoading,
				userInfo,
				menuItems
			}"
			@click:menu-item="onMenuItemClick"
		>
			<RouterView />
		</BLayout>
	</PermissionRouteGuard>
</template>
